/**
 * Design tokens for marketing components
 * Centralized color system for feature cards, icons, buttons, and other marketing UI elements
 */

export type FeatureColor =
  | 'blue'
  | 'purple'
  | 'green'
  | 'red'
  | 'pink'
  | 'yellow'
  | 'cyan'
  | 'indigo';

export interface ColorConfig {
  from: string;
  to: string;
  shadow: string;
  hoverShadow: string;
  hoverBorder: string;
}

export const FEATURE_COLORS: Record<FeatureColor, ColorConfig> = {
  blue: {
    from: 'from-blue-600',
    to: 'to-blue-700',
    shadow: 'shadow-blue-600/30',
    hoverShadow: 'hover:shadow-blue-600/10',
    hoverBorder: 'hover:border-blue-600/30',
  },
  purple: {
    from: 'from-purple-600',
    to: 'to-purple-700',
    shadow: 'shadow-purple-600/30',
    hoverShadow: 'hover:shadow-purple-600/10',
    hoverBorder: 'hover:border-purple-600/30',
  },
  green: {
    from: 'from-green-500',
    to: 'to-green-600',
    shadow: 'shadow-green-500/30',
    hoverShadow: 'hover:shadow-green-500/10',
    hoverBorder: 'hover:border-green-500/30',
  },
  red: {
    from: 'from-red-500',
    to: 'to-red-600',
    shadow: 'shadow-red-500/30',
    hoverShadow: 'hover:shadow-red-500/10',
    hoverBorder: 'hover:border-red-500/30',
  },
  pink: {
    from: 'from-pink-600',
    to: 'to-pink-700',
    shadow: 'shadow-pink-600/30',
    hoverShadow: 'hover:shadow-pink-600/10',
    hoverBorder: 'hover:border-pink-600/30',
  },
  yellow: {
    from: 'from-yellow-500',
    to: 'to-yellow-600',
    shadow: 'shadow-yellow-500/30',
    hoverShadow: 'hover:shadow-yellow-500/10',
    hoverBorder: 'hover:border-yellow-500/30',
  },
  cyan: {
    from: 'from-cyan-500',
    to: 'to-cyan-600',
    shadow: 'shadow-cyan-500/30',
    hoverShadow: 'hover:shadow-cyan-500/10',
    hoverBorder: 'hover:border-cyan-500/30',
  },
  indigo: {
    from: 'from-indigo-500',
    to: 'to-indigo-600',
    shadow: 'shadow-indigo-500/30',
    hoverShadow: 'hover:shadow-indigo-500/10',
    hoverBorder: 'hover:border-indigo-500/30',
  },
} as const;

/**
 * Button gradient configurations for primary action buttons
 */
export interface ButtonGradientConfig {
  base: string;
  hover: string;
  shadow: string;
  hoverShadow: string;
}

export const BUTTON_GRADIENTS = {
  primary: {
    base: 'bg-gradient-to-r from-blue-600 to-purple-700',
    hover: 'hover:from-blue-700 hover:to-purple-800',
    shadow: 'shadow-lg shadow-blue-600/20',
    hoverShadow: 'hover:shadow-xl hover:shadow-blue-600/30',
  },
  purple: {
    base: 'bg-gradient-to-r from-purple-600 to-pink-700',
    hover: 'hover:from-purple-700 hover:to-pink-800',
    shadow: 'shadow-lg shadow-purple-600/30',
    hoverShadow: 'hover:shadow-xl hover:shadow-purple-600/40',
  },
} as const;

/**
 * CTA banner gradient configurations
 */
export interface CTAGradientConfig {
  base: string;
  blur: string;
  shadow: string;
}

// CTA banner now uses a solid brand surface instead of a gradient for a
// cleaner, less templated look. Field names retained for compatibility.
export const CTA_GRADIENTS: CTAGradientConfig = {
  base: 'bg-blue-700',
  blur: 'bg-blue-700',
  shadow: 'shadow-2xl shadow-blue-900/20',
} as const;
